/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IPivotStyleProps, IPivotStyles, Pivot, PivotItem } from '@fluentui/react/lib/Pivot';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useHistory, useLocation } from 'react-router';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { logPivotClick } from 'src/core/services/telemetry/eventsNamesTemplate';
import { getStylist } from 'src/core/themes/luisStylist';
import { routeTable } from 'src/core/utils/routeTable';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { QnaMakerEditQuestionAnswerPairs } from 'src/qnaMaker/appShell/qnaMakerEditPage/QnaMakerEditQuestionAnswerPairs';
import { QnaMakerEditSynonyms } from 'src/qnaMaker/appShell/qnaMakerEditPage/QnaMakerEditSynonyms';

const messages = defineMessages({
    questions: {
        id: 'qnaMaker.qnaMakerEditknowledgeBase.questions',
        defaultMessage: 'Question answer pairs'
    },
    synonyms: {
        id: 'qnaMaker.qnaMakerEditknowledgeBase.synonyms',
        defaultMessage: 'Synonyms'
    }
});

const { getClassName } = getStylist('QnaMakerEditKnowledgeBase');
const pivotRootClassName = getClassName('PivotRoot', {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    overflow: 'hidden',
    height: '100%'
});
const pivotItemClassName = getClassName('PivotItem', { height: '100%' });
const pivotStyles = classNamesFunction<IPivotStyleProps, IPivotStyles>()({
    root: { marginBottom: 15 },
    itemContainer: { overflow: 'hidden', height: '100%' }
});

export const QnaMakerEditKnowledgeBase = observer(() => {
    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();
    const currentProjectName = rootStore.currentQnaMakerProjectStore?.name;
    const history = useHistory();
    const location = useLocation();
    const { serviceName } = getPortalFromUrl();
    const searchParams = new URLSearchParams(location.search);
    const activeTab = decodeURIComponent(searchParams.get('assets'));

    return (
        <Pivot
            linkSize="large"
            styles={pivotStyles}
            className={pivotRootClassName}
            selectedKey={activeTab}
            onLinkClick={item => {
                logPivotClick(serviceName, 'EditKb', item.props.itemKey, rootStore.currentQnaMakerProjectStore.id);
                history.push(`${routeTable.qnaMaker.editPage(currentProjectName)}?assets=${item.props.itemKey}`);
            }}>
            <PivotItem itemKey="qnas" headerText={localize(messages.questions)} className={pivotItemClassName}>
                <QnaMakerEditQuestionAnswerPairs />
            </PivotItem>

            <PivotItem itemKey="synonyms" headerText={localize(messages.synonyms)} className={pivotItemClassName}>
                <QnaMakerEditSynonyms />
            </PivotItem>
        </Pivot>
    );
});
